
import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  Building2, 
  MapPin, 
  Clock, 
  Users, 
  Music, 
  Instagram, 
  Facebook, 
  Globe,
  Edit3,
  Save,
  Camera,
  Plus,
  X
} from "lucide-react";

export const VenueProfilePage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [venueData, setVenueData] = useState({
    name: "Skyline Lounge",
    description: "Premium rooftop venue with panoramic city views and state-of-the-art sound system",
    address: "123 Downtown Ave, City Center",
    capacity: 500,
    phone: "+1 (555) 123-4567",
    email: "info@skylinelounge.com",
    website: "www.skylinelounge.com",
    musicGenres: ["House", "Techno", "Deep House", "Progressive"],
    amenities: ["Rooftop", "VIP Area", "Full Bar", "Dance Floor", "View"],
    operatingHours: {
      monday: { open: "18:00", close: "02:00", closed: false },
      tuesday: { open: "18:00", close: "02:00", closed: false },
      wednesday: { open: "18:00", close: "02:00", closed: false },
      thursday: { open: "18:00", close: "03:00", closed: false },
      friday: { open: "18:00", close: "04:00", closed: false },
      saturday: { open: "18:00", close: "04:00", closed: false },
      sunday: { open: "16:00", close: "02:00", closed: false },
    },
    socialMedia: {
      instagram: "@skylinelounge",
      facebook: "SkylineLoungeOfficial",
      twitter: "@skylinelounge"
    }
  });

  const [newGenre, setNewGenre] = useState("");
  const [newAmenity, setNewAmenity] = useState("");

  const handleSave = () => {
    // Here you would typically save to your backend
    setIsEditing(false);
  };

  const addGenre = () => {
    if (newGenre.trim() && !venueData.musicGenres.includes(newGenre.trim())) {
      setVenueData(prev => ({
        ...prev,
        musicGenres: [...prev.musicGenres, newGenre.trim()]
      }));
      setNewGenre("");
    }
  };

  const removeGenre = (genre: string) => {
    setVenueData(prev => ({
      ...prev,
      musicGenres: prev.musicGenres.filter(g => g !== genre)
    }));
  };

  const addAmenity = () => {
    if (newAmenity.trim() && !venueData.amenities.includes(newAmenity.trim())) {
      setVenueData(prev => ({
        ...prev,
        amenities: [...prev.amenities, newAmenity.trim()]
      }));
      setNewAmenity("");
    }
  };

  const removeAmenity = (amenity: string) => {
    setVenueData(prev => ({
      ...prev,
      amenities: prev.amenities.filter(a => a !== amenity)
    }));
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div className="flex items-center gap-2">
          <Building2 className="h-6 w-6 text-purple-400" />
          <h1 className="text-2xl font-bold">Venue Profile</h1>
        </div>
        <Button 
          onClick={isEditing ? handleSave : () => setIsEditing(true)}
          variant={isEditing ? "default" : "outline"}
        >
          {isEditing ? (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Changes
            </>
          ) : (
            <>
              <Edit3 className="mr-2 h-4 w-4" />
              Edit Profile
            </>
          )}
        </Button>
      </div>

      {/* Cover Photo Section */}
      <Card className="glass-card overflow-hidden">
        <div className="relative h-48 bg-gradient-to-r from-purple-900 to-pink-900">
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute bottom-4 left-4 z-10">
            <div className="flex items-center gap-4">
              <div className="w-20 h-20 bg-white/10 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Building2 className="h-10 w-10 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">{venueData.name}</h2>
                <p className="text-white/90">{venueData.description}</p>
              </div>
            </div>
          </div>
          {isEditing && (
            <Button variant="secondary" size="sm" className="absolute top-4 right-4">
              <Camera className="h-4 w-4 mr-2" />
              Change Cover
            </Button>
          )}
        </div>
      </Card>

      <Tabs defaultValue="basic" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="basic">Basic Info</TabsTrigger>
          <TabsTrigger value="hours">Operating Hours</TabsTrigger>
          <TabsTrigger value="features">Features</TabsTrigger>
          <TabsTrigger value="social">Social Media</TabsTrigger>
        </TabsList>

        <TabsContent value="basic" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Venue Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="name">Venue Name</Label>
                  <Input
                    id="name"
                    value={venueData.name}
                    onChange={(e) => setVenueData(prev => ({ ...prev, name: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={venueData.description}
                    onChange={(e) => setVenueData(prev => ({ ...prev, description: e.target.value }))}
                    disabled={!isEditing}
                    rows={3}
                  />
                </div>
                <div>
                  <Label htmlFor="address">Address</Label>
                  <Input
                    id="address"
                    value={venueData.address}
                    onChange={(e) => setVenueData(prev => ({ ...prev, address: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="capacity">Capacity</Label>
                  <Input
                    id="capacity"
                    type="number"
                    value={venueData.capacity}
                    onChange={(e) => setVenueData(prev => ({ ...prev, capacity: parseInt(e.target.value) }))}
                    disabled={!isEditing}
                  />
                </div>
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Contact Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label htmlFor="phone">Phone</Label>
                  <Input
                    id="phone"
                    value={venueData.phone}
                    onChange={(e) => setVenueData(prev => ({ ...prev, phone: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={venueData.email}
                    onChange={(e) => setVenueData(prev => ({ ...prev, email: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>
                <div>
                  <Label htmlFor="website">Website</Label>
                  <Input
                    id="website"
                    value={venueData.website}
                    onChange={(e) => setVenueData(prev => ({ ...prev, website: e.target.value }))}
                    disabled={!isEditing}
                  />
                </div>
                <div className="pt-4">
                  <div className="flex items-center gap-3 text-sm text-muted-foreground">
                    <MapPin className="h-4 w-4" />
                    <span>{venueData.address}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm text-muted-foreground mt-2">
                    <Users className="h-4 w-4" />
                    <span>Capacity: {venueData.capacity}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="hours" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Clock className="h-5 w-5" />
                Operating Hours
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {Object.entries(venueData.operatingHours).map(([day, hours]) => (
                  <div key={day} className="flex items-center gap-4">
                    <div className="w-24 capitalize font-medium">
                      {day}
                    </div>
                    <div className="flex items-center gap-2 flex-1">
                      <Input
                        type="time"
                        value={hours.open}
                        onChange={(e) => setVenueData(prev => ({
                          ...prev,
                          operatingHours: {
                            ...prev.operatingHours,
                            [day]: { ...hours, open: e.target.value }
                          }
                        }))}
                        disabled={!isEditing || hours.closed}
                        className="w-32"
                      />
                      <span>to</span>
                      <Input
                        type="time"
                        value={hours.close}
                        onChange={(e) => setVenueData(prev => ({
                          ...prev,
                          operatingHours: {
                            ...prev.operatingHours,
                            [day]: { ...hours, close: e.target.value }
                          }
                        }))}
                        disabled={!isEditing || hours.closed}
                        className="w-32"
                      />
                      <Button
                        variant={hours.closed ? "outline" : "destructive"}
                        size="sm"
                        onClick={() => setVenueData(prev => ({
                          ...prev,
                          operatingHours: {
                            ...prev.operatingHours,
                            [day]: { ...hours, closed: !hours.closed }
                          }
                        }))}
                        disabled={!isEditing}
                      >
                        {hours.closed ? "Open" : "Closed"}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="features" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Music className="h-5 w-5" />
                  Music Genres
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {venueData.musicGenres.map((genre) => (
                    <Badge key={genre} variant="secondary" className="flex items-center gap-1">
                      {genre}
                      {isEditing && (
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-red-500" 
                          onClick={() => removeGenre(genre)}
                        />
                      )}
                    </Badge>
                  ))}
                </div>
                {isEditing && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add music genre"
                      value={newGenre}
                      onChange={(e) => setNewGenre(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addGenre()}
                    />
                    <Button onClick={addGenre} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            <Card className="glass-card">
              <CardHeader>
                <CardTitle>Amenities</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {venueData.amenities.map((amenity) => (
                    <Badge key={amenity} variant="outline" className="flex items-center gap-1">
                      {amenity}
                      {isEditing && (
                        <X 
                          className="h-3 w-3 cursor-pointer hover:text-red-500" 
                          onClick={() => removeAmenity(amenity)}
                        />
                      )}
                    </Badge>
                  ))}
                </div>
                {isEditing && (
                  <div className="flex gap-2">
                    <Input
                      placeholder="Add amenity"
                      value={newAmenity}
                      onChange={(e) => setNewAmenity(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && addAmenity()}
                    />
                    <Button onClick={addAmenity} size="sm">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="social" className="space-y-6">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Social Media Links</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="instagram" className="flex items-center gap-2">
                    <Instagram className="h-4 w-4" />
                    Instagram
                  </Label>
                  <Input
                    id="instagram"
                    value={venueData.socialMedia.instagram}
                    onChange={(e) => setVenueData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, instagram: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="@username"
                  />
                </div>
                <div>
                  <Label htmlFor="facebook" className="flex items-center gap-2">
                    <Facebook className="h-4 w-4" />
                    Facebook
                  </Label>
                  <Input
                    id="facebook"
                    value={venueData.socialMedia.facebook}
                    onChange={(e) => setVenueData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, facebook: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="Page name"
                  />
                </div>
                <div>
                  <Label htmlFor="twitter" className="flex items-center gap-2">
                    <Globe className="h-4 w-4" />
                    Twitter/X
                  </Label>
                  <Input
                    id="twitter"
                    value={venueData.socialMedia.twitter}
                    onChange={(e) => setVenueData(prev => ({
                      ...prev,
                      socialMedia: { ...prev.socialMedia, twitter: e.target.value }
                    }))}
                    disabled={!isEditing}
                    placeholder="@username"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};
